/**
 * O que toda tela precisa saber: em que barbearia estamos e quem está logado.
 *
 * A barbearia carrega primeiro e sem depender de login — é dela que sai o tema,
 * o nome e a logo da tela de entrada.
 */

import type { TenantTheme } from '@barbearia/core';
import type { SessionContext, TenantPublicView, ThemeMode } from '@barbearia/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { api } from './api.ts';
import {
  applyHomeScreenIcon,
  applyTheme,
  resolveMode,
  storePreference,
  watchSystemMode,
} from './theme.ts';

interface TenantResponse {
  tenant: TenantPublicView;
  theme: TenantTheme;
}

interface TenantValue {
  tenant: TenantPublicView;
  theme: TenantTheme;
  mode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
}

const TenantCtx = createContext<TenantValue | null>(null);
const SessionCtx = createContext<SessionContext | null>(null);

export function useTenant(): TenantValue {
  const value = useContext(TenantCtx);
  if (!value) throw new Error('useTenant fora do provedor');
  return value;
}

/** Fuso da barbearia — atalho, porque quase toda tela precisa. */
export function useTimezone(): string {
  return useTenant().tenant.timezone;
}

export function useSession(): SessionContext | null {
  return useContext(SessionCtx);
}

export function useIsStaff(): boolean {
  return useSession()?.membership != null;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['tenant'],
    queryFn: () => api.get<TenantResponse>('/tenant'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const [mode, setResolvedMode] = useState<'light' | 'dark'>('dark');

  useEffect(() => {
    if (!data) return;
    const resolved = resolveMode(data.tenant.themeMode);
    setResolvedMode(resolved);
    applyTheme(data.theme, resolved);
    // O ícone da tela de início precisa estar no documento antes de alguém
    // tocar em "Adicionar" — o iOS lê o que está no DOM naquele instante.
    applyHomeScreenIcon(data.tenant.iconUrl ?? data.tenant.logoUrl);
  }, [data]);

  useEffect(() => {
    if (!data) return;
    // Só acompanha o sistema quando ninguém fixou um tema.
    return watchSystemMode((system) => {
      const resolved = resolveMode(data.tenant.themeMode);
      if (resolved === system) {
        setResolvedMode(system);
        applyTheme(data.theme, system);
      }
    });
  }, [data]);

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-surface">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 bg-surface p-8 text-center">
        <h1 className="text-lg font-semibold text-ink">Barbearia não encontrada</h1>
        <p className="max-w-xs text-sm text-ink-soft">
          Confira o endereço. Cada barbearia tem o seu, no formato{' '}
          <span className="whitespace-nowrap font-medium">nome-da-barbearia.dominio</span>.
        </p>
      </div>
    );
  }

  const setMode = (next: ThemeMode) => {
    storePreference(next);
    const resolved = resolveMode(data.tenant.themeMode);
    setResolvedMode(resolved);
    applyTheme(data.theme, resolved);
  };

  return (
    <TenantCtx.Provider value={{ tenant: data.tenant, theme: data.theme, mode, setMode }}>
      {children}
    </TenantCtx.Provider>
  );
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, isPending } = useQuery({
    queryKey: ['session'],
    queryFn: () => api.get<SessionContext>('/auth/session'),
    retry: false,
    staleTime: 30 * 1000,
  });

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-surface">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-primary" />
      </div>
    );
  }

  const session = data?.user ? data : null;

  return <SessionCtx.Provider value={session}>{children}</SessionCtx.Provider>;
}

/** Depois de entrar ou sair, tudo que dependia de quem sou eu é recarregado. */
export function useRefreshSession(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries();
  };
}
